import { memo, useState, useRef, useEffect, useCallback } from "react";
import { ClassmateInfo } from "shared/types/sociometry";
import { classNames } from "shared/lib/classNames/classNames";
import cls from "./SociometrySelect.module.scss";

interface SociometrySelectProps {
    classmates: ClassmateInfo[];
    selectedIds: string[];
    disabledIds: string[];
    onSelect: (id: string) => void;
    placeholder?: string;
    className?: string;
}

const formatFullName = (classmate: ClassmateInfo): string => {
    const parts = [classmate.surname, classmate.name];
    if (classmate.patronymic) {
        parts.push(classmate.patronymic);
    }
    return parts.join(" ");
};

export const SociometrySelect = memo((props: SociometrySelectProps) => {
    const {
        classmates,
        selectedIds,
        disabledIds,
        onSelect,
        placeholder = "Выберите одноклассника",
        className,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedClassmate = classmates.find((c) => selectedIds.includes(c._id));

    const filteredClassmates = classmates.filter((classmate) => {
        const fullName = formatFullName(classmate).toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
    });

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
            setSearchTerm("");
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    const handleToggle = () => {
        if (selectedClassmate) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    const handleSelect = (id: string) => {
        onSelect(id);
        setIsOpen(false);
        setSearchTerm("");
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect("");
        setSearchTerm("");
    };

    return (
        <div className={classNames(cls.sociometrySelect, {}, [className])} ref={dropdownRef}>
            <div
                className={classNames(cls.selectTrigger, { [cls.open]: isOpen, [cls.hasValue]: !!selectedClassmate })}
                onClick={handleToggle}
            >
                {selectedClassmate ? (
                    <div className={cls.selectedValue}>
                        <span>{formatFullName(selectedClassmate)}</span>
                        <button
                            type="button"
                            className={cls.clearBtn}
                            onClick={handleClear}
                            aria-label="Clear selection"
                        >
                            ×
                        </button>
                    </div>
                ) : (
                    <span className={cls.placeholder}>{placeholder}</span>
                )}
                {!selectedClassmate && <span className={classNames(cls.arrow, { [cls.arrowUp]: isOpen })} />}
            </div>

            {isOpen && (
                <div className={cls.dropdown}>
                    <div className={cls.searchWrapper}>
                        <input
                            ref={inputRef}
                            type="text"
                            className={cls.searchInput}
                            placeholder="Поиск..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <ul className={cls.optionsList}>
                        {filteredClassmates.length === 0 ? (
                            <li className={cls.noResults}>Ничего не найдено</li>
                        ) : (
                            filteredClassmates.map((classmate) => {
                                const isDisabled = disabledIds.includes(classmate._id);
                                const isSelected = selectedIds.includes(classmate._id);

                                return (
                                    <li
                                        key={classmate._id}
                                        className={classNames(cls.option, {
                                            [cls.disabled]: isDisabled,
                                            [cls.selected]: isSelected,
                                        })}
                                        onClick={() => !isDisabled && handleSelect(classmate._id)}
                                    >
                                        {formatFullName(classmate)}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
});

SociometrySelect.displayName = "SociometrySelect";
